import { useState } from 'react'
import { isEmpty, reduce } from 'lodash'

import { ChangePencil } from '../../../../components/SvgIcon'
import { replaceKey } from '../../../../utils/helpers'

import styles from './RecipeCategory.module.css'

// The chapter name, with a pencil to rename it (the name is printed in the book)
const ChapterName = ({ onChange, value, name, title, setTitle, setCategories }) => {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(title)

  const save = () => {
    const newName = draft.trim()
    if (!isEmpty(newName) && newName !== title) {
      onChange(replaceKey({ ...value }, name, newName))
      setCategories((oldState) =>
        reduce(
          oldState,
          (res, val, key) =>
            key === title ? { ...res, [newName]: oldState[title] } : { ...res, [key]: val },
          {}
        )
      )
      setTitle(newName)
    }
    setEditing(false)
  }

  if (editing) {
    return (
      <div className={styles.rename}>
        <input
          autoFocus
          onFocus={(e) => e.target.select()}
          className="cbInput"
          value={draft}
          maxLength={60}
          aria-label="Chapter name"
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              save()
            }
            if (e.key === 'Escape') setEditing(false)
          }}
        />
        <button type="button" className="cbSecondaryButton" onClick={save}>
          Save
        </button>
        <button type="button" className={styles.cancel} onClick={() => setEditing(false)}>
          Cancel
        </button>
      </div>
    )
  }

  return (
    <h3 className={styles.name}>
      {title}
      <button
        type="button"
        className={styles.pencil}
        aria-label={`Rename the ${title} chapter`}
        title="Rename this chapter"
        onClick={() => {
          setDraft(title)
          setEditing(true)
        }}
      >
        <ChangePencil />
      </button>
    </h3>
  )
}

const RecipeCategory = ({
  onChange,
  value,
  data,
  name,
  categoryId,
  setCategories,
  defaultOpen = false,
}) => {
  const [title, setTitle] = useState(name)
  const [open, setOpen] = useState(defaultOpen)
  const allSelectedValuesId = reduce(value, (res, val) => [...res, ...val.recipes], [])
  const chosen = value[title]?.recipes?.length || 0
  const isSelectedCategory = chosen === data.length
  const tickState = isSelectedCategory ? styles.tickAll : chosen ? styles.tickSome : ''

  const selectCategoryRecipe = (isActive) => {
    if (!isActive) {
      return onChange({ ...value, [name]: { ...value[name], recipes: [] } })
    }
    const recipes = data.map(({ id }) => id)
    setOpen(true)
    return onChange({ ...value, [name]: { ...value[name], recipes } })
  }

  // The printed book credits "Grandma Rose" when a family member added her recipe for her
  const RecipeTile = ({ name, id, image, author, credited_name }) => {
    const isSelected = value?.[title]?.recipes?.includes(id)
    const isDuplicated =
      allSelectedValuesId.filter((idSelectedRecipe) => idSelectedRecipe === id).length >= 2 &&
      isSelected

    const selectRecipe = () => {
      const recipes = isSelected
        ? value[title]?.recipes.filter((recipe) => recipe !== id)
        : [...value[title]?.recipes, id]

      return onChange({ ...value, [title]: { recipes, categoryId } })
    }

    return (
      <label
        className={`${styles.tile} ${isSelected ? styles.tileOn : ''} ${
          isDuplicated ? styles.tileDuplicate : ''
        }`}
      >
        <input type="checkbox" checked={!!isSelected} onChange={selectRecipe} />
        <span className={styles.tick} aria-hidden="true" />
        <img src={image} alt="" className={styles.photo} />
        <span className={styles.tileText}>
          <b>{name}</b>
          {credited_name || author?.fullname ? (
            <span className={styles.author}>{credited_name || author.fullname}</span>
          ) : null}
          {isDuplicated ? (
            <span className={styles.duplicateNote}>
              Also chosen in another chapter. Untick one so it isn’t printed twice.
            </span>
          ) : null}
        </span>
      </label>
    )
  }

  return (
    <section className={`cbSection ${styles.category}`}>
      <div className={styles.head}>
        <button
          type="button"
          className={`${styles.tickButton} ${tickState}`}
          aria-label={
            isSelectedCategory ? `Untick all recipes in ${title}` : `Choose all recipes in ${title}`
          }
          aria-pressed={isSelectedCategory}
          onClick={() => selectCategoryRecipe(!isSelectedCategory)}
        />
        <div className={styles.headText}>
          <ChapterName
            data={data}
            name={name}
            value={value}
            title={title}
            setTitle={setTitle}
            onChange={onChange}
            setCategories={setCategories}
          />
          <p className="cbMuted">
            {chosen} of {data.length} chosen
          </p>
        </div>
        <button
          type="button"
          className={styles.toggle}
          aria-expanded={open}
          onClick={() => setOpen((oldState) => !oldState)}
        >
          {open ? 'Hide' : `Show ${data.length}`}
          <span className={`${styles.chevron} ${open ? styles.chevronUp : ''}`} />
        </button>
      </div>
      {open ? (
        <div className={styles.tiles}>
          {data.map((item) => (
            <RecipeTile key={item.id} {...item} />
          ))}
        </div>
      ) : null}
    </section>
  )
}

export default RecipeCategory
